import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Loader2, ArrowLeft, Copy, Check, Tag, ChevronRight } from 'lucide-react';
import type { SelfServicePromotion, SelfServiceSubmissionRow } from '../../types/selfService';
import { useSingleStationContext } from '../../components/self-service/admin/useAdminContext';
import { publisherStatusBadge } from '../../components/self-service/campaignSharedHelpers';
import { apiClient } from '../../lib/apiClient';

type SubmissionRow = Pick<
  SelfServiceSubmissionRow,
  'id' | 'business_info' | 'contact_email' | 'campaign' | 'amount_cents'
  | 'status' | 'created_at' | 'promo_code'
>;

const PublisherPromotionDetailPage: React.FC = () => {
  const { slug } = useSingleStationContext();
  const { promotionId } = useParams<{ promotionId: string }>();
  if (!promotionId) return <Navigate to={`/admin/${slug}/promotions`} replace />;
  return <Inner slug={slug} promotionId={promotionId} />;
};

const Inner: React.FC<{ slug: string; promotionId: string }> = ({ slug, promotionId }) => {
  const [promo, setPromo] = React.useState<SelfServicePromotion | null>(null);
  const [submissions, setSubmissions] = React.useState<SubmissionRow[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [copied, setCopied] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [pData, subData] = await Promise.all([
          apiClient.post<{ rows: SelfServicePromotion[] }>('/promotions/list', { slug }),
          apiClient.post<{ rows: SubmissionRow[] }>('/submissions/list-by-publisher', { slug }),
        ]);
        if (cancelled) return;
        const found = (pData.rows ?? []).find((r) => r.id === promotionId) ?? null;
        setPromo(found);
        if (found) {
          const all = subData.rows ?? [];
          setSubmissions(all.filter((s) => (s.promo_code ?? null) === found.code));
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug, promotionId]);

  const shareUrl = promo
    ? `${typeof window !== 'undefined' ? window.location.origin : ''}/${slug}?promo=${promo.code}`
    : '';

  const handleCopy = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* ignore */ }
  };

  // Roll-up. Under the new bonus-plays model, the discount is delivered as
  // extra ad plays at full price — there's no $ discount given to back-
  // calculate. TODO: surface "bonus plays delivered" once per-submission
  // CPM is queryable from this page.
  const stats = React.useMemo(() => {
    if (!promo) return null;
    // Status enum migrated: legacy 'captured' → new schema's 'approved'.
    const captured = submissions.filter((s) => s.status === 'approved');
    const capturedRevenue = captured.reduce((acc, s) => acc + s.amount_cents, 0);
    return {
      total: submissions.length,
      capturedCount: captured.length,
      capturedRevenueDollars: capturedRevenue / 100,
    };
  }, [submissions, promo]);

  return (
    <div className="max-w-5xl">
      <Link to={`/admin/${slug}/promotions`} className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 mb-4">
        <ArrowLeft className="w-4 h-4" /> Back to promotions
      </Link>

        {loading ? (
          <div className="text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-700">{error}</div>
        ) : !promo || !stats ? (
          <div className="bg-white border border-slate-200 rounded-xl p-10 text-center">
            <Tag className="w-8 h-8 text-slate-400 mx-auto mb-2" />
            <p className="text-slate-700 font-medium">Promotion not found</p>
          </div>
        ) : (
          <>
            <div className="flex items-start justify-between gap-4 mb-6">
              <div>
                <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">Promotion</div>
                <div className="flex items-center gap-3 mt-1">
                  <code className="text-2xl font-mono font-semibold text-slate-900 tracking-widest">{promo.code}</code>
                  <span className="text-base font-semibold text-slate-700">+{promo.discount_percent}% bonus plays</span>
                </div>
                <div className="mt-2 text-sm text-slate-600">
                  {describeWindow(promo)}
                  {promo.max_uses != null && <> · Max {promo.max_uses} use{promo.max_uses === 1 ? '' : 's'}</>}
                </div>
              </div>
              <button
                type="button"
                onClick={handleCopy}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 hover:bg-slate-50 rounded-md whitespace-nowrap"
              >
                {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                {copied ? 'Copied' : 'Copy share link'}
              </button>
            </div>

            <div className="grid grid-cols-3 gap-4 mb-6">
              <Stat label="Total redemptions" value={stats.total.toString()} />
              <Stat label="Approved" value={stats.capturedCount.toString()} />
              <Stat label="Approved revenue" value={`$${stats.capturedRevenueDollars.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} />
            </div>

            <div className="mb-3">
              <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Redemptions</h2>
            </div>

            {submissions.length === 0 ? (
              <div className="bg-white border border-slate-200 rounded-xl p-10 text-center">
                <p className="text-slate-700 font-medium">No redemptions yet</p>
                <p className="text-sm text-slate-500 mt-1">When an advertiser uses this code, their campaign will show up here.</p>
              </div>
            ) : (
              <ul className="space-y-2">
                {submissions.map((s) => {
                  const total = (s.amount_cents / 100).toFixed(2);
                  return (
                    <li key={s.id}>
                      <Link
                        to={`/admin/${slug}/submissions/${s.id}`}
                        className="flex items-center justify-between gap-3 bg-white border border-slate-200 rounded-xl p-4 hover:border-slate-400 hover:shadow-sm transition-all"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-base font-semibold text-slate-900 truncate">
                              {s.business_info?.businessName || 'Advertiser'}
                            </span>
                            <span className={`text-xs font-medium px-2 py-0.5 rounded ${publisherStatusBadge(s.status).className}`}>
                              {publisherStatusBadge(s.status).label}
                            </span>
                          </div>
                          <div className="text-xs text-slate-500">
                            ${total} · {new Date(s.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                          </div>
                        </div>
                        <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0" />
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
        </>
      )}
    </div>
  );
};

function describeWindow(p: SelfServicePromotion): string {
  if (!p.starts_at && !p.ends_at) return 'Always active';
  const fmt = (s: string | null) =>
    s ? new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
  if (p.starts_at && p.ends_at) return `${fmt(p.starts_at)} – ${fmt(p.ends_at)}`;
  if (p.starts_at) return `From ${fmt(p.starts_at)}`;
  return `Until ${fmt(p.ends_at)}`;
}

const Stat: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-5">
    <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">{label}</div>
    <div className="text-2xl font-bold text-slate-900">{value}</div>
  </div>
);

export default PublisherPromotionDetailPage;
